import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';

/**
 * Toast notifications — the replacement for ~100 `alert()` calls.
 *
 * `alert()` was being used for every success and failure message in the app.
 * It blocks the page, is styled by the browser (and announces "localhost:4028
 * says"), ignores the design system, can't be dismissed by anything but a
 * click, and is genuinely unpleasant on mobile.
 *
 * Design decisions:
 *   - Errors DO NOT auto-dismiss. If something failed, the user must have the
 *     chance to read it; a message that vanishes after 4s is how people end up
 *     not knowing why a save didn't work.
 *   - Success auto-dismisses. Confirmation is transient by nature.
 *   - `role="alert"` + `aria-live="assertive"` for errors (interrupt), and
 *     `role="status"` + `aria-live="polite"` for everything else, so screen
 *     readers get the same priority distinction sighted users get from colour.
 *   - Anchored to the bottom-INLINE-start, so it mirrors under RTL.
 *   - Sits above the consent bar, and never covers it.
 */

const ToastContext = createContext(null);

let idSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) { clearTimeout(timer); timers.current.delete(id); }
  }, []);

  const push = useCallback((message, { variant = 'info', duration } = {}) => {
    if (!message) return null;
    const id = ++idSeq;
    // Errors persist until dismissed; everything else fades.
    const ms = duration ?? (variant === 'error' ? null : 5000);
    setToasts((list) => [...list, { id, message: String(message), variant }]);
    if (ms) timers.current.set(id, setTimeout(() => dismiss(id), ms));
    return id;
  }, [dismiss]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);

  const api = useMemo(() => ({
    toast: push,
    success: (m, o) => push(m, { ...o, variant: 'success' }),
    error: (m, o) => push(m, { ...o, variant: 'error' }),
    info: (m, o) => push(m, { ...o, variant: 'info' }),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const VARIANT = {
  success: { icon: 'CheckCircle2', ring: 'border-success-border', tint: 'bg-success-subtle', mark: 'text-success' },
  error:   { icon: 'AlertCircle',  ring: 'border-error-border',   tint: 'bg-error-subtle',   mark: 'text-error' },
  info:    { icon: 'Info',         ring: 'border-border',         tint: 'bg-card',           mark: 'text-primary' },
};

function ToastViewport({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed bottom-16 z-[2147482000] flex flex-col gap-2 max-w-[min(24rem,calc(100vw-2rem))]"
      style={{ insetInlineStart: '1rem' }}
    >
      {toasts.map((t) => {
        const v = VARIANT[t.variant] || VARIANT.info;
        const assertive = t.variant === 'error';
        return (
          <div
            key={t.id}
            role={assertive ? 'alert' : 'status'}
            aria-live={assertive ? 'assertive' : 'polite'}
            className={`flex items-start gap-2.5 ps-3 pe-2 py-2.5 rounded-lg border ${v.ring} ${v.tint} shadow-elevation-3`}
          >
            <Icon name={v.icon} size={16} className={`${v.mark} mt-0.5 flex-shrink-0`} />
            <p className="text-sm text-foreground leading-snug flex-1 min-w-0 break-words">{t.message}</p>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              className="flex-shrink-0 w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="X" size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * useToast() — inside components.
 *
 * Falls back to a no-throw shim if the provider is missing, so a component
 * rendered outside the tree (tests, storybook) degrades to the console rather
 * than crashing the page.
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  const warn = (m) => console.warn('[toast] no ToastProvider mounted:', m);
  return { toast: warn, success: warn, error: warn, info: warn, dismiss: () => {} };
}

export default ToastProvider;
